/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosElementLearnImageCardFragment } from "@cosmos/hygraph-schema";

import {
	CosmosCard,
	CosmosImage,
	CosmosRichText,
	CosmosText,
	CosmosTextVariantEnum,
	EdsIcon,
} from "@components/elements";
import { toShortNum } from "@components/utils";
import { cx, css } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { vstack } from "@panda/patterns";
import { SystemStyleObject } from "@panda/types";

import { Source } from "./Source.molecule";
import { useHygraphImageLoader } from "@components/hooks";

const cardStyle: SystemStyleObject = {
	w: "full",
	lgTo2xl: {
		h: "full",
	},
};

export type LearnCardProps = CosmosElementLearnImageCardFragment & {
	squareImage?: boolean;
	upVotesCount?: number;
	threadCommentsCount?: number;
};

export function LearnCard({
	img,
	imageLabel,
	source,
	title,
	desc,
	url,
	squareImage,
	upVotesCount,
	threadCommentsCount,
}: LearnCardProps): React.JSX.Element {
	const hygraphImageLoader = useHygraphImageLoader();

	return (
		<CosmosCard
			padding={16}
			className={cx(
				"learn-card",
				vstack({
					direction: "column",
					alignItems: "flex-start",
					justifyContent: "stretch",
					gap: "eds.dimension.16",
				}),
				css(cardStyle),
			)}
			variant={"material"}
		>
			{img ? (
				<CosmosCard.Thumbnail aspectRatio={squareImage ? "1/1" : "5/3"}>
					<CosmosImage
						alt={imageLabel || ""}
						className={"image"}
						src={img?.url}
						width={400}
						height={squareImage ? 400 : 225}
						loader={hygraphImageLoader}
					/>
				</CosmosCard.Thumbnail>
			) : (
				""
			)}
			<VStack gap={"eds.dimension.2"} alignItems={"start"}>
				<CosmosCard.Link href={url || ""} className={css({ width: "100%", textAlign: "start" })}>
					<CosmosRichText
						variant={CosmosTextVariantEnum.uiMD}
						html={title?.html}
						className={css({
							fontWeight: "bold",
						})}
					/>
				</CosmosCard.Link>
				{source && (source.author || source.timestamp) ? <Source {...source}></Source> : null}
				<CosmosRichText
					variant={CosmosTextVariantEnum.paragraphSM}
					color={"eds.text.secondary"}
					html={desc}
					className={css({
						lineClamp: 2,
						display: "-webkit-box",
						boxOrient: "vertical",
						overflow: "hidden",
					})}
				/>
			</VStack>
			{!!upVotesCount && !!threadCommentsCount && (
				<HStack gap={"eds.dimension.16"}>
					<HStack gap={"eds.dimension.4"}>
						<EdsIcon icon={"ThumbUp"} color={"secondary"} size={"sm"} />
						<CosmosText variant={CosmosTextVariantEnum.uiMD} color={"eds.text.primary"}>
							{toShortNum(upVotesCount)}
						</CosmosText>
					</HStack>
					<HStack gap={"eds.dimension.4"}>
						<EdsIcon icon={"TalkBubbleDots"} color={"secondary"} size={"sm"} />
						<CosmosText variant={CosmosTextVariantEnum.uiMD} color={"eds.text.primary"}>
							{toShortNum(threadCommentsCount)}
						</CosmosText>
					</HStack>
				</HStack>
			)}
		</CosmosCard>
	);
}
